/**
 * AG Grid Cell Renderers — module-scope, stable identity, never recreated.
 *
 * All renderers are React.memo to prevent unnecessary re-renders.
 * TimeRenderer uses `key` prop (via cellRendererParams) for external resets.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  TIME_PENALTIES,
  pad2,
  clamp,
  isRowError,
  isUnknownSail,
  computeTimeValidation,
} from "./constants";

// ─── Touch tap helper ────────────────────────────────────────────────────────
// Fire the action directly on touchend (with a small movement tolerance)
// instead of waiting for the browser's synthesized click. On a busy grid
// (200–500 rows) the synthetic click arrives late — after a re-render has
// moved/replaced the tapped element — so taps needed 2–3 tries or landed on a
// shifted row, and any finger drift past the browser's own slop cancelled the
// click entirely ("hard press" symptom). preventDefault() on touchend
// suppresses the late synthetic click (no double fire); onClick still serves
// mouse and keyboard.
const TAP_SLOP_PX = 14;
const useTapHandlers = (action) => {
  const startRef = useRef(null);
  return {
    onTouchStart: (e) => {
      const t = e.touches[0];
      startRef.current = t ? { x: t.clientX, y: t.clientY } : null;
    },
    onTouchEnd: (e) => {
      const start = startRef.current;
      startRef.current = null;
      if (!start) return;
      const t = e.changedTouches[0];
      if (!t) return;
      if (
        Math.abs(t.clientX - start.x) > TAP_SLOP_PX ||
        Math.abs(t.clientY - start.y) > TAP_SLOP_PX
      )
        return; // finger moved — treat as scroll, not a tap
      e.preventDefault();
      e.stopPropagation();
      action(e);
    },
    onTouchCancel: () => {
      startRef.current = null;
    },
    onClick: (e) => {
      e.stopPropagation();
      action(e);
    },
  };
};

// ─── Position badge (circle with row number) ────────────────────────────────
export const PositionRenderer = React.memo(({ data, context }) => {
  const compactTablet = !!context?.compactTablet;
  const bg = isRowError(data) ? "#e53935" : "#43a047";
  // Show the row's REAL finish position (stable) — not a display index — so
  // sorting the grid doesn't renumber the rows.
  const pos = data?.position ?? "";
  const sz = compactTablet ? 18 : 22;
  return (
    <Box
      sx={{
        width: sz,
        height: sz,
        borderRadius: "50%",
        bgcolor: bg,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: compactTablet ? 10 : 11,
        fontWeight: 700,
        flexShrink: 0,
        pt: "2px",
      }}
    >
      {pos}
    </Box>
  );
});

// ─── Commit-then-Tab focus helper (finished grid) ───────────────────────────
// A commit's dispatch can REDRAW the row after the keydown: a rowEl captured
// at keydown detaches (focusing it silently does nothing → the old "press Tab
// twice" bug), and even a successfully-focused node can be destroyed a few
// frames later. So the target is resolved FRESH each attempt, and re-asserted
// once if focus fell back to the page (never when the user owns focus).
const focusAfterCommit = (resolve) => {
  setTimeout(() => {
    resolve()?.focus();
    const reassert = () => {
      const ae = document.activeElement;
      if (!ae || ae === document.body || !ae.isConnected) resolve()?.focus();
    };
    setTimeout(reassert, 180);
    // A save-VERDICT flip (server response) can land later than the commit's
    // own redraw and destroy the focused node — one more guarded re-assert
    // covers that window. Both only fire when focus was truly lost.
    setTimeout(reassert, 550);
  }, 0);
};
const finishedRowSel = (idx) =>
  document.querySelector(`.nft-finished-grid .ag-row[row-index="${idx}"]`);

// First interactive field of a finished-grid row, honouring locked/disabled
// cells. A fully locked row (abandoned: PLC/sail/time/penalty all inert)
// yields null — callers skip it and keep walking to the next editable row.
const firstFieldOfRow = (rowEl) => {
  if (!rowEl) return null;
  const place = rowEl.querySelector(".nft-place-input input");
  if (place && !place.disabled && !place.readOnly) return place;
  const sail = rowEl.querySelector(".nft-sailnum-btn");
  if (sail && sail.tabIndex !== -1) return sail;
  const time = rowEl.querySelector(".nft-time-btn");
  if (time && time.tabIndex !== -1) return time;
  // Abandoned rows render a static ABN label — no Select → null (skip row).
  return rowEl.querySelector(".MuiSelect-select") || null;
};

// Nearest interactive delete (×) button at or above fromIdx — abandoned rows'
// × is tabIndex -1 (inert), so walk further back instead of dead-ending there.
const prevDeleteBtn = (fromIdx) => {
  for (let idx = fromIdx; idx >= 0; idx--) {
    const rowEl = finishedRowSel(idx);
    if (!rowEl) return null; // ran out of rendered rows
    const btn = rowEl.querySelector(".nft-delete-btn");
    if (btn && btn.tabIndex !== -1) return btn;
  }
  return null;
};

// ─── Editable sail number (finished grid) ───────────────────────────────────
export const FinishedSailRenderer = React.memo(({ value, data, context }) => {
  // Sail is red ONLY for a sail issue (unknown/unmatched entrant). Time problems
  // are shown on the row background, not the sail (matches the old manual tab).
  const isError = isUnknownSail(data);
  const canEdit = isUnknownSail(data) && !data?.isAbandon;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => {
    if (!editing) setVal(value || "");
  }, [value, editing]);

  const commit = () => {
    const v = (val || "").trim();
    setEditing(false);
    if (!v) {
      // Cleared: old screen shows "Please define sail number" and reverts —
      // the handler toasts; local state snaps back to the previous value.
      if ((value || "").trim()) context?.onSailNumSave?.(data.id, "");
      setVal(value || "");
      return;
    }
    if (v.toLowerCase() !== (value || "").toLowerCase()) {
      context?.onSailNumSave?.(data.id, v);
    }
  };

  if (canEdit && editing) {
    return (
      <TextField
        autoFocus
        value={val}
        size="small"
        // 8-char cap. maxLength hard-stops desktop typing; tablet IMEs ignore
        // it mid-composition, so the controlled slice ALSO caps state — the
        // field can never show more than 8 chars on any platform.
        inputProps={{ maxLength: 8 }}
        onChange={(e) => setVal(e.target.value.toUpperCase().slice(0, 8))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") {
            setEditing(false);
            setVal(value || "");
          }
          if (e.key === "Tab" && !e.shiftKey) {
            e.preventDefault();
            const idx = e.target.closest(".ag-row")?.getAttribute("row-index");
            commit();
            focusAfterCommit(() => {
              const rowEl = finishedRowSel(idx);
              const timeBtn = rowEl?.querySelector(".nft-time-btn");
              return timeBtn && timeBtn.tabIndex !== -1
                ? timeBtn
                : rowEl?.querySelector(".MuiSelect-select");
            });
          }
          if (e.key === "Tab" && e.shiftKey) {
            // Reverse hop: place mode → this row's PLC input (the cell before
            // the sail); time mode (sail is the row's first stop) → previous
            // row's ×.
            e.preventDefault();
            const idx = parseInt(
              e.target.closest(".ag-row")?.getAttribute("row-index") ?? "-1",
              10,
            );
            commit();
            focusAfterCommit(
              () =>
                finishedRowSel(idx)?.querySelector(".nft-place-input input") ||
                prevDeleteBtn(idx - 1),
            );
          }
        }}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: 12,
            fontWeight: 700,
            padding: "2px 4px",
            color: "#e53935",
          },
        }}
      />
    );
  }

  return (
    <Typography
      variant="body2"
      tabIndex={canEdit ? 0 : -1}
      className="nft-sailnum-btn"
      title={canEdit ? "Click to correct sail #" : value || ""}
      onClick={canEdit ? () => setEditing(true) : undefined}
      onFocus={() => {
        if (canEdit && !editing) {
          setEditing(true);
        }
      }}
      sx={{
        fontSize: 12,
        color: isError ? "#e53935" : "inherit",
        fontWeight: isError ? 700 : "normal",
        cursor: canEdit ? "pointer" : "default",
        textDecoration: canEdit ? "underline" : "none",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {(value || "").toUpperCase() || "—"}
    </Typography>
  );
});

// ─── Inline HH:MM:SS time editor ───────────────────────────────────────────
export const TimeRenderer = React.memo(({ data, context }) => {
  const compact768 = !!context?.compact768;
  const compactTablet = !!context?.compactTablet;
  // Overnight shares the row with an extra DATE column, so the TIME column is
  // narrower there → the display box must fit the cell instead of its natural
  // min width (which spilled over DATE with the cell's overflow:visible).
  const isOvernight = !!context?.overnight;
  const isPenaltyDisabled =
    !!(data?.dn && !TIME_PENALTIES.includes(data.dn)) || data?.isAbandon;
  const hasTime = data?.hr != null && data?.min != null && data?.sec != null;

  const [editing, setEditing] = useState(false);
  const [hr, setHr] = useState(pad2(data?.hr));
  const [min, setMin] = useState(pad2(data?.min));
  const [sec, setSec] = useState(pad2(data?.sec));
  const hrRef = useRef(null);
  const minRef = useRef(null);
  const secRef = useRef(null);
  const skipBlurRef = useRef(false);

  const focusInput = (ref) =>
    ref.current?.querySelector("input")?.focus() ?? ref.current?.focus();
  const tabTo = (ref) => {
    skipBlurRef.current = true;
    focusInput(ref);
    requestAnimationFrame(() => {
      skipBlurRef.current = false;
    });
  };
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const startEditing = (e) => {
    if (isPenaltyDisabled || editing) return;
    e.stopPropagation();
    setEditing(true);
    setTimeout(() => {
      if (mountedRef.current && hrRef.current) focusInput(hrRef);
    }, 0);
  };

  const commit = useCallback(() => {
    // If a field was cleared, revert to its previous persisted value and flag
    // as error so the box highlights red (both valid and invalid sail numbers).
    const hrEmpty = hr === "";
    const minEmpty = min === "";
    const secEmpty = sec === "";

    const ph = hrEmpty ? pad2(data?.hr ?? 0) : clamp(hr, 23);
    const pm = minEmpty ? pad2(data?.min ?? 0) : clamp(min, 59);
    const ps = secEmpty ? pad2(data?.sec ?? 0) : clamp(sec, 59);

    const hrNum = parseInt(hr, 10) || 0;
    const minNum = parseInt(min, 10) || 0;
    const secNum = parseInt(sec, 10) || 0;
    // Flag error if field was cleared OR value exceeded allowed range
    const changedHr = hrEmpty || hrNum > 23;
    const changedMin = minEmpty || minNum > 59;
    const changedSec = secEmpty || secNum > 59;
    setHr(ph);
    setMin(pm);
    setSec(ps);
    setEditing(false);
    // Single-source-of-truth: parent persists per-field error flags onto row
    // data (errorHR/errorMin/errorSec). AG Grid refreshes the cell via row
    // data change — no local clamped state needed.
    context.onTimeBlur(data.id, "hr", ph, changedHr);
    context.onTimeBlur(data.id, "min", pm, changedMin);
    context.onTimeBlur(data.id, "sec", ps, changedSec);
  }, [hr, min, sec, data.id, data.hr, data.min, data.sec, context]);

  const displayTime = hasTime
    ? { hr: pad2(data.hr), min: pad2(data.min), sec: pad2(data.sec) }
    : null;

  // Single-source-of-truth validation (see constants.computeTimeValidation).
  const v = computeTimeValidation(data, context?.fleetDetails);
  const showHrError = v.hrError;
  const showMinError = v.minError;
  const showSecError = v.secError;
  const showBoxError = v.anyError;

  if (!editing || isPenaltyDisabled) {
    return (
      <Box
        className="nft-time-btn"
        tabIndex={isPenaltyDisabled ? -1 : 0}
        onClick={startEditing}
        onFocus={startEditing}
        onKeyDown={(e) => {
          if (e.key === "Tab") {
            e.preventDefault();
            const rowEl = e.target.closest(".ag-row");
            if (e.shiftKey) {
              rowEl?.querySelector(".nft-sailnum-btn")?.focus();
            } else {
              rowEl?.querySelector(".MuiSelect-select")?.focus();
            }
          }
        }}
        sx={{
          cursor: isPenaltyDisabled ? "default" : "pointer",
          px: isOvernight ? 0.75 : 1,
          py: 0.25,
          borderRadius: 1,
          border: "1px solid",
          borderColor: isPenaltyDisabled
            ? "transparent"
            : showBoxError
              ? "#e53935"
              : hasTime
                ? "#356dad55"
                : "#ccc",
          bgcolor: isPenaltyDisabled
            ? "transparent"
            : showBoxError
              ? "#ffebee"
              : hasTime
                ? "#f0f6ff"
                : "#fafafa",
          color: showBoxError ? "#e53935" : hasTime ? "#1a3a5c" : "#aaa",
          fontSize: compactTablet ? 12 : 13,
          fontWeight: hasTime ? 600 : 400,
          // No extra tracking on tablet — it pushed the seconds against the box
          // edge / DATE column in the (narrower) overnight cell.
          letterSpacing: compactTablet ? 0 : 1,
          // Fill the cell exactly in ALL cases. Previously only the overnight
          // cell used width:100%; non-overnight used a bare minWidth, so the
          // read-only box looked much narrower than the (cell-centered) edit
          // inputs. Filling the cell makes the view and edit footprints match.
          width: "100%",
          minWidth: isOvernight ? 0 : compact768 ? 70 : compactTablet ? 78 : 90,
          maxWidth: "100%",
          boxSizing: "border-box",
          // View-mode time value sits centered inside the box.
          textAlign: "center",
          userSelect: "none",
          outline: "none",
          "&:focus-visible": isPenaltyDisabled
            ? {}
            : { outline: "2px solid #356dad", outlineOffset: 1 },
          "&:hover": isPenaltyDisabled
            ? {}
            : { borderColor: "#356dad", bgcolor: "#eaf2ff" },
        }}
      >
        {displayTime ? (
          <Box
            component="span"
            sx={{ display: "inline-flex", gap: "2px", alignItems: "center" }}
          >
            <Box
              component="span"
              sx={{
                color: showHrError ? "#e53935" : hasTime ? "#1a3a5c" : "#aaa",
                fontWeight: showHrError ? 700 : 600,
              }}
            >
              {displayTime.hr}
            </Box>
            <Box
              component="span"
              sx={{ color: showBoxError ? "#e53935" : "#666" }}
            >
              :
            </Box>
            <Box
              component="span"
              sx={{
                color: showMinError ? "#e53935" : hasTime ? "#1a3a5c" : "#aaa",
                fontWeight: showMinError ? 700 : 600,
              }}
            >
              {displayTime.min}
            </Box>
            <Box
              component="span"
              sx={{ color: showBoxError ? "#e53935" : "#666" }}
            >
              :
            </Box>
            <Box
              component="span"
              sx={{
                color: showSecError ? "#e53935" : hasTime ? "#1a3a5c" : "#aaa",
                fontWeight: showSecError ? 700 : 600,
              }}
            >
              {displayTime.sec}
            </Box>
          </Box>
        ) : (
          "HH:MM:SS"
        )}
      </Box>
    );
  }

  // Each field holds only 2 digits, so keep the inputs narrow — the old 40px
  // desktop width made the edit row sprawl across the (wide) non-overnight cell.
  const fieldSx = {
    width: compact768 ? 24 : compactTablet ? 26 : 30,
    "& .MuiInputBase-input": {
      textAlign: "center",
      padding: "2px 1px",
      fontSize: compactTablet ? 11 : 12,
    },
  };
  const errorFieldSx = (isError) => ({
    ...fieldSx,
    ...(isError && {
      "& .MuiInputBase-input": {
        ...fieldSx["& .MuiInputBase-input"],
        color: "#e53935",
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e53935" },
    }),
  });
  const hrInvalid = hr !== "" && Number(hr) > 23;
  const minInvalid = min !== "" && Number(min) > 59;
  const secInvalid = sec !== "" && Number(sec) > 59;

  return (
    <Box
      // Center the HH:MM:SS inputs to match the centered read-only time box.
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onBlur={(e) => {
        if (skipBlurRef.current) return;
        if (!e.currentTarget.contains(e.relatedTarget)) commit();
      }}
    >
      <TextField
        value={hr}
        inputRef={hrRef}
        size="small"
        inputProps={{ maxLength: 2, inputMode: "numeric", pattern: "[0-9]*" }}
        sx={errorFieldSx(hrInvalid)}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          if (/^\d*$/.test(e.target.value)) {
            setHr(e.target.value);
            if (e.target.value.length === 2) tabTo(minRef);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            context.onEntryCommitDone?.(); 
          }
          if (e.key === "Escape") setEditing(false);
          if (e.key === "Tab") {
            e.preventDefault();
            tabTo(minRef);
          }
        }}
      />
      <Typography sx={{ fontSize: 12, color: "#666" }}>:</Typography>
      <TextField
        value={min}
        inputRef={minRef}
        size="small"
        inputProps={{ maxLength: 2, inputMode: "numeric", pattern: "[0-9]*" }}
        sx={errorFieldSx(minInvalid)}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          if (/^\d*$/.test(e.target.value)) {
            setMin(e.target.value);
            if (e.target.value.length === 2) tabTo(secRef);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            context.onEntryCommitDone?.();
          }
          if (e.key === "Escape") setEditing(false);
          if (e.key === "Tab") {
            e.preventDefault();
            tabTo(secRef);
          }
        }}
      />
      <Typography sx={{ fontSize: 12, color: "#666" }}>:</Typography>
      <TextField
        value={sec}
        inputRef={secRef}
        size="small"
        inputProps={{ maxLength: 2, inputMode: "numeric", pattern: "[0-9]*" }}
        sx={errorFieldSx(secInvalid)}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          if (/^\d*$/.test(e.target.value)) {
            setSec(e.target.value);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            context.onEntryCommitDone?.();
          }
          if (e.key === "Escape") setEditing(false);
          if (e.key === "Tab" && !e.shiftKey) {
            e.preventDefault();
            const idx = e.target.closest(".ag-row")?.getAttribute("row-index");
            commit();
            // Overnight: DATE sits between TIME and PENALTY — visit it when
            // present + unlocked (.nft-date-btn only exists in overnight,
            // so non-overnight behavior is unchanged).
            focusAfterCommit(() => {
              const rowEl = finishedRowSel(idx);
              const dateBtn = rowEl?.querySelector(".nft-date-btn");
              return dateBtn && !dateBtn.disabled && dateBtn.tabIndex !== -1
                ? dateBtn
                : rowEl?.querySelector(".MuiSelect-select");
            });
          }
        }}
      />
    </Box>
  );
});

// ─── Penalty dropdown ───────────────────────────────────────────────────────
// `changeHandler` (cellRendererParams) picks the context callback — defaults to
// the finished grid's; the place-mode About grid passes "onAboutPenaltyChange".
export const PenaltyRenderer = React.memo(
  ({ data, context, changeHandler }) => {
    const compact768 = !!context?.compact768;
    const compactTablet = !!context?.compactTablet;
    const boxRef = useRef(null);
    const onChange = context?.[changeHandler || "onPenaltyChange"];
    // Controlled open for the keyboard flow: focus arriving on the closed select
    // (Tab chain) OPENS the menu. The suppress flag stops MUI's own post-close
    // refocus of the select from instantly re-opening it.
    const [open, setOpen] = useState(false);
    const suppressOpenRef = useRef(false);

    // Abandon rows: show static label, no interaction. Left-aligned with the
    // same text inset as the penalty Select, so ABN lines up with the other
    // penalty values in the column.
    if (data?.isAbandon) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            pl: compactTablet ? "6px" : "14px",
            width: "100%",
            height: "100%",
          }}
        >
          <Typography sx={{ fontSize: compact768 ? 11 : 12 }}>ABN</Typography>
        </Box>
      );
    }

    // Move focus out of the penalty cell. Used by BOTH the closed-select Tab
    // capture and the open-menu Tab close (MUI reports reason "tabKeyDown").
    // A penalty commit can reset this row's hr/min/sec to "00" (non-time DN),
    // which changes TimeRenderer's key and remounts it — same as a slow
    // saveRow response landing late can redraw this row's cells after the
    // user has already moved on. Every OTHER renderer's Tab-away handler
    // guards against that (focusAfterCommit's fresh-query + 180/550ms
    // reassert); this one used a single un-reasserted setTimeout(0) and could
    // silently drop focus. Resolve by row-index (not a captured element) so
    // each attempt re-queries the current DOM, same as the other renderers.
    const tabHop = (backwards) => {
      const idx = boxRef.current?.closest(".ag-row")?.getAttribute("row-index");
      if (backwards) {
        // Reverse = visual predecessor: overnight DATE first (.nft-date-btn only
        // exists in overnight), then time cell (time mode), then the editable
        // sail (invalid/phantom row, place mode), else the PLC input.
        focusAfterCommit(() => {
          const rowEl = finishedRowSel(idx);
          const dateBtn = rowEl?.querySelector(".nft-date-btn");
          const sail = rowEl?.querySelector(".nft-sailnum-btn");
          return (
            (dateBtn && !dateBtn.disabled && dateBtn.tabIndex !== -1
              ? dateBtn
              : null) ||
            rowEl?.querySelector(".nft-time-btn") ||
            (sail && sail.tabIndex !== -1 ? sail : null) ||
            rowEl?.querySelector(".nft-place-input input")
          );
        });
      } else {
        focusAfterCommit(() =>
          finishedRowSel(idx)?.querySelector(".nft-delete-btn"),
        );
      }
    };
    // Close the menu and keep MUI's post-close refocus of the select from
    // instantly re-opening it.
    const closeMenu = () => {
      setOpen(false);
      suppressOpenRef.current = true;
      setTimeout(() => {
        suppressOpenRef.current = false;
      }, 250);
    };
    const handleTabCapture = (e) => {
      if (e.key !== "Tab") return;
      e.preventDefault();
      e.stopPropagation();
      // Focus can sit on the select DISPLAY while the menu is open — close it
      // ourselves before hopping.
      if (open) closeMenu();
      tabHop(e.shiftKey);
    };
    return (
      <Box
        ref={boxRef}
        sx={{ width: "100%", minWidth: 0 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDownCapture={handleTabCapture}
      >
        <Select
          value={data?.dn || ""}
          size="small"
          displayEmpty
          // Tab flow: arriving on the closed select opens the menu; Tab inside
          // the OPEN menu → MUI closes it (reason "tabKeyDown") → hop straight
          // to the next field in the same press.
          open={open}
          onOpen={() => setOpen(true)}
          onClose={(event, reason) => {
            closeMenu();
            // Belt: if this close DID come from a Tab (Menu reports reason
            // "tabKeyDown", but the Select wrapper often swallows it and only
            // forwards the keydown event), hop as well.
            if (reason === "tabKeyDown" || event?.key === "Tab") {
              tabHop(!!event?.shiftKey);
            } else if (event?.key === "Escape" || reason === "escapeKeyDown") {
              // Escape: stay on the penalty cell.
              setTimeout(
                () =>
                  boxRef.current?.querySelector(".MuiSelect-select")?.focus(),
                0,
              );
            } else {
              if (!context.onEntryCommitDone?.()) tabHop(false);
            }
          }}
          onFocus={() => {
            if (!suppressOpenRef.current) setOpen(true);
          }}
          // Direct Tab handling INSIDE the open menu (the portal — our Box
          // capture handler can't see it). Don't depend on MUI's internal Tab
          // close surfacing to onClose: close + hop ourselves. If MUI also
          // closes, the duplicate hop targets the same node — harmless.
          MenuProps={{
            // Prevent MUI from restoring focus to the Select after the menu
            // closes — that restore races against our tabHop and re-triggers
            // onFocus → re-opens the menu. We manage focus ourselves in onClose.
            disableRestoreFocus: true,
            onKeyDown: (e) => {
              if (e.key !== "Tab") return;
              e.preventDefault();
              e.stopPropagation();
              closeMenu();
              tabHop(e.shiftKey);
            },
          }}
          onChange={(e) => {
            // Suppress the onFocus re-open: MUI returns focus to the Select
            // before onClose fires, so without this the menu would re-open
            // immediately after the user picks a value.
            suppressOpenRef.current = true;
            setTimeout(() => {
              suppressOpenRef.current = false;
            }, 250);
            onChange?.(data.id, e.target.value);
          }}
          sx={{
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            height: compactTablet ? 24 : 28,
            fontSize: compact768 ? 11 : 12,
            "& .MuiSelect-select": {
              py: "2px",
              // Left inset trimmed so the value (e.g. DGM) lines up under the
              // "PENALTY" header like the FLEET column instead of sitting
              // indented by MUI's default 14px input padding.
              pl: "6px !important",
              // MUI's default 32px right padding (for the dropdown arrow) left too
              // much dead space next to the 3-char value; trim on desktop.
              pr: "20px !important",
              ...(compactTablet && {
                padding: "0 !important",
                textAlign: "center",
                mr: "5px",
              }),
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiSelect-icon": compactTablet
              ? { width: 16, height: 16, right: 2, top: 5 }
              : { width: 16, height: 16, right: 2, top: 5 },
          }}
        >
          <MenuItem value="" sx={{ fontSize: 12 }}></MenuItem>
          {(context.penalties || []).map((p) => (
            <MenuItem
              key={p.penaltyId || p.penaltyName}
              value={p.penaltyName}
              sx={{ fontSize: 12 }}
            >
              {p.penaltyName}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  },
);

// ─── Delete button (finished grid + About drop) ─────────────────────────────
// `handler` (cellRendererParams) picks the context callback — defaults to the
// finished grid's delete; the place-mode About grid passes "onAboutDrop".
export const DeleteRenderer = React.memo(({ data, context, handler }) => {
  const compactTablet = !!context?.compactTablet;
  const isAbandoned = data?.isAbandon;
  const onAction = context?.[handler || "onDeleteFinished"];
  const tap = useTapHandlers(() => {
    if (!isAbandoned) onAction?.(data);
  });
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.stopPropagation();
      onAction?.(data);
    }
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      const rowEl = e.target.closest(".ag-row");
      const currentIdx = parseInt(rowEl?.getAttribute("row-index") ?? "-1", 10);
      // Walk DOWN past rows with no focusable field (abandoned rows are fully
      // locked) and land on the first field of the next EDITABLE row —
      // stopping dead on a locked row broke the Tab flow mid-grid.
      for (let idx = currentIdx + 1; ; idx++) {
        const nextRow = document.querySelector(
          `.nft-finished-grid .ag-row[row-index="${idx}"]`,
        );
        if (!nextRow) return; // no more rendered rows below
        const target = firstFieldOfRow(nextRow);
        if (target) {
          target.focus();
          return;
        }
      }
    }
  };
  const dot = compactTablet ? 17 : 22;
  return (
    // Outer element fills the whole cell so the tap target is the full cell
    // (not just the 17px dot). touchAction:manipulation stops Android from
    // treating the tap as scroll-intent and cancelling it (the Galaxy Tab
    // "needs 3-4 taps" bug). Confined to the cell so it can't overlap the
    // penalty select or an adjacent row.
    <Box
      className="nft-delete-btn"
      tabIndex={isAbandoned ? -1 : 0}
      role="button"
      {...tap}
      onKeyDown={isAbandoned ? undefined : handleKeyDown}
      title={isAbandoned ? "Cannot delete abandoned entry" : "Delete"}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isAbandoned ? "not-allowed" : "pointer",
        userSelect: "none",
        outline: "none",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
        ...(!isAbandoned && {
          "&:focus-visible .nft-delete-dot": { boxShadow: "0 0 0 2px #1976d2" },
          "&:hover .nft-delete-dot": { bgcolor: "#b71c1c" },
        }),
      }}
    >
      <Box
        className="nft-delete-dot"
        sx={{
          width: dot,
          height: dot,
          borderRadius: "50%",
          bgcolor: isAbandoned ? "#bdbdbd" : "#e53935",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        <CloseIcon sx={{ fontSize: 12 }} />
      </Box>
    </Box>
  );
});

// ─── Unfinished sail# button ────────────────────────────────────────────────
export const UnfinishedSailRenderer = React.memo(({ value, data, context }) => {
  const compact768 = !!context?.compact768;
  const compactTablet = !!context?.compactTablet;
  const displayValue = (value || "").toUpperCase();
  const label = context.snWithAlpha
    ? displayValue
    : displayValue.replace(/[A-Za-z]/g, "");
  // Abandoned entrants cannot be moved/captured (old screen disabled the button)
  const isAbandoned = !!data?.isAbandon;
  const tap = useTapHandlers(() => {
    if (isAbandoned) return;
    if (context.mode === "real-time") context.onAddToWatchlist(data);
    else context.onUnfinishedClick(data);
  });
  return (
    <Button
      size="small"
      disabled={isAbandoned}
      {...tap}
      title={
        isAbandoned
          ? "Abandoned"
          : context.mode === "real-time"
            ? `Add ${label} to "About to Finish"`
            : label
      }
      sx={{
        bgcolor: isAbandoned ? "#bdbdbd" : "#f0a500",
        color: "#fff",
        borderRadius: 20,
        // Tighter horizontal padding on smaller screens so the pill can show a
        // full 8-char sail (e.g. "RED0293") instead of ellipsing to "RED…".
        px: compact768 ? 0.5 : compactTablet ? 0.75 : 1.5,
        py: 0.25,
        fontSize: compactTablet ? 12 : 12,
        fontWeight: 600,
        textTransform: "none",
        // minWidth: compact768 ? 38 : compactTablet ? 42 : 70,
        // maxWidth: compact768 ? 38 : compactTablet ? 42 : 70,
        width: "90%",
        touchAction: "manipulation",
        minHeight: 24,
        WebkitTapHighlightColor: "transparent",
        "&:hover": { bgcolor: isAbandoned ? "#bdbdbd" : "#d48900" },
        "&.Mui-disabled": { bgcolor: "#bdbdbd", color: "#fff" },
      }}
    >
      <Box
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          // Allow ~8 chars before ellipsing on every screen size.
          maxWidth: compact768 ? 60 : compactTablet ? 74 : 100,
        }}
      >
        {label}
      </Box>
    </Button>
  );
});

// ─── Green action button ("Finish" capture / About "Save") ──────────────────
// `handler`/`label` (cellRendererParams) default to the real-time capture
// button; the place-mode About grid passes { handler: "onAboutSave", label: "Save" }.
export const NowRenderer = React.memo(({ data, context, handler, label }) => {
  // Abandoned entrants cannot capture a finish (old screen disabled NOW)
  const isAbandoned = !!data?.isAbandon;
  const onAction = context?.[handler || "onCaptureNow"];
  const tap = useTapHandlers(() => {
    if (!isAbandoned) onAction?.(data);
  });
  return (
    <Button
      size="small"
      disabled={isAbandoned}
      {...tap}
      title={
        isAbandoned
          ? "Abandoned"
          : label
            ? "Save to Finished"
            : "Capture finish time now"
      }
      sx={{
        bgcolor: isAbandoned ? "#bdbdbd" : "#43a047",
        color: "#fff",
        borderRadius: 20,
        px: context?.compact768 ? 1 : 1.5,
        py: 0.25,
        pt: 0.15,
        fontSize: context?.compact768 ? 11 : 12,
        fontWeight: 600,
        textTransform: "none",
        minWidth: context?.compact768 ? 36 : context?.compactTablet ? 40 : 60,
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
        "&:hover": { bgcolor: isAbandoned ? "#bdbdbd" : "#2e7d32" },
        "&.Mui-disabled": { bgcolor: "#bdbdbd", color: "#fff" },
      }}
    >
      {label || "Finish"}
    </Button>
  );
});

// ─── Static text cells ──────────────────────────────────────────────────────
export const TextCell = (p) => (
  <span
    title={p.value || ""}
    style={{
      display: "block",
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {p.value || ""}
  </span>
);

export const BoatCell = (p) => (
  <span
    title={p.value || ""}
    style={{
      display: "block",
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {p.value || "—"}
  </span>
);

// ─── Place input (Place-series scoring) ─────────────────────────────────────
// `changeHandler` (cellRendererParams) picks the context callback (finished
// grid default / About grid "onAboutPlaceChange"). `allowClear` relaxes the
// clear-needs-DN rule — the old About place cell allows blanking freely.
export const PlaceRenderer = React.memo(
  ({ data, context, changeHandler, allowClear }) => {
    const compact768 = !!context?.compact768;
    const compactTablet = !!context?.compactTablet;
    const initial =
      data?.place === 0 || data?.place == null ? "" : String(data.place);
    const [val, setVal] = useState(initial);
    const commit = () => {
      const n = parseInt(val, 10);
      const place = isNaN(n) || n <= 0 ? "" : n;
      // Old-screen rule: a finished place can only be cleared when a DN is set
      // (the row must always carry place OR DN). Without a DN: snap back to the
      // existing place, or no-op if the row has none yet — never forward an
      // empty place (which would save an incomplete row).
      if (!allowClear && place === "" && !data?.dn) {
        setVal(data?.place ? String(data.place) : "");
        console.log(
          data?.dn,
          "data?.dn value",
          data?.place,
          "data?.place value",
        );
        return;
      }
      setVal(place === "" ? "" : String(place));
      context?.[changeHandler || "onPlaceChange"]?.(data.id, place);
    };
    // Abandoned rows: static value, no editing (old screen: editable !isAbandon)
    if (data?.isAbandon) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: compact768 ? 40 : compactTablet ? 46 : 80,
          }}
        >
          <Typography
            sx={{ fontSize: compactTablet ? 11 : 13, fontWeight: 700 }}
          >
            {initial || "—"}
          </Typography>
        </Box>
      );
    }
    return (
      <TextField
        value={val}
        size="small"
        placeholder=""
        className="nft-place-input"
        inputProps={{ inputMode: "numeric", maxLength: 3 }}
        onChange={(e) => {
          // Digits only, max 3 — the length guard also caps a pasted value.
          const v = e.target.value;
          if (/^\d*$/.test(v) && v.length <= 3) setVal(v);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            e.target.blur?.();
            context.onEntryCommitDone?.(); 
          }
          // Excel-like Tab chain (old screen: Tab moved PLC → DN → next row's
          // PLC via AG Grid editing): forward → this row's penalty select;
          // backward → previous row's delete ×.
          if (e.key === "Tab") {
            e.preventDefault();
            e.stopPropagation();
            // Read the row index BEFORE commit: committing a place can flip the
            // row red→normal (editable:false) and redraw the row, detaching any
            // element captured now — focusing that dead node was why the first
            // Tab appeared to do nothing. Resolve the target FRESH by index at
            // focus time, with one retry after the redraw settles.
            const idx = parseInt(
              e.target.closest(".ag-row")?.getAttribute("row-index") ?? "-1",
              10,
            );
            commit();
            const rowSel = `.nft-finished-grid .ag-row[row-index="${idx}"]`;
            const tryFocus = () => {
              let el;
              if (e.shiftKey) {
                // Skip abandoned rows' inert × — nearest interactive one above.
                el = prevDeleteBtn(idx - 1);
              } else {
                // Forward: an EDITABLE sail (invalid/phantom row) sits between PLC
                // and penalty in the visual order — visit it; else penalty select.
                const sail = document.querySelector(
                  `${rowSel} .nft-sailnum-btn`,
                );
                el =
                  sail && sail.tabIndex !== -1
                    ? sail
                    : document.querySelector(`${rowSel} .MuiSelect-select`);
              }
              el?.focus();
              return !!el && document.activeElement === el;
            };
            setTimeout(() => {
              if (!tryFocus()) setTimeout(tryFocus, 80);
              // The commit can flip the row red→white and REDRAW it AFTER the
              // first focus already landed — the focused node is destroyed and
              // focus falls back to the page, which made a second Tab necessary.
              // Re-assert once after the redraw settles, but only when focus was
              // actually lost (never fight the user's own focus).
              setTimeout(() => {
                const ae = document.activeElement;
                if (!ae || ae === document.body || !ae.isConnected) tryFocus();
              }, 180);
            }, 0);
          }
        }}
        sx={{
          width: compact768 ? 40 : compactTablet ? 46 : 34,
          minWidth: compact768 ? 40 : compactTablet ? 46 : 34,
          maxWidth: compact768 ? 40 : compactTablet ? 46 : 34,
          "& .MuiInputBase-root": { width: "100%" },
          "& .MuiInputBase-input": {
            textAlign: "left",
            padding: "1px",
            placeItems: "center",
            fontSize: compactTablet ? 11 : 13,
            fontWeight: 700,
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          },
        }}
      />
    );
  },
);

// ─── Finish date (overnight races) ──────────────────────────────────────────
// Native date input (default segmented typing UX), but edits live in LOCAL
// state and the value commits ONCE on blur/Enter/Tab. Committing per native
// change event (fires per completed segment) re-rendered/remounted the cell
// mid-typing — losing segments and closing the mini-calendar — do NOT
// reintroduce it. lang="en-CA" makes Chromium render/type the control as
// YYYY-MM-DD regardless of the system locale (a date input cannot otherwise
// be format-forced). `.nft-date-btn` keeps the input in the row's Tab chain
// (TIME → DATE → DN).
export const DateRenderer = React.memo(({ data, context }) => {
  const compact768 = !!context?.compact768;
  const compactTablet = !!context?.compactTablet;
  // Locked for abandoned rows and non-time penalties (DNC etc.) — matches the
  // old screen's date-cell editable rule.
  const locked =
    !!data?.isAbandon || !!(data?.dn && !TIME_PENALTIES.includes(data.dn));
  const [val, setVal] = useState(data?.finishDate || "");
  const focusedRef = useRef(false);
  const dateInputRef = useRef(null);
  // Last value we actually pushed up. Dedupes the native-change + blur pair
  // (both fire on type-then-blur) so we dispatch once, and it survives the prop
  // lag between dispatch and the re-render that updates data.finishDate.
  const committedRef = useRef(data?.finishDate || "");

  // Sync external changes (DNC clears the date, restores, reload) while the
  // input is not being edited.
  useEffect(() => {
    if (!focusedRef.current) {
      setVal(data?.finishDate || "");
      committedRef.current = data?.finishDate || "";
    }
  }, [data?.finishDate]);

  // Force ALL segments back to placeholders. A partially-typed date (e.g. only
  // the month deleted) makes input.value = "" while the OTHER segments keep
  // their text in the DOM — React can't clear them (controlled value is already
  // "") so it must be done imperatively.
  const clearSegments = () => {
    if (dateInputRef.current) dateInputRef.current.value = "";
  };

  // Commit a SPECIFIC value — state updates are async, so the picker path
  // passes the fresh value straight from the change event. Compares against
  // committedRef (not data.finishDate) so the same value never dispatches twice.
  const commitValue = (raw) => {
    const v = (raw || "").trim();
    if (v === "") {
      // Empty OR partially-typed date → clear EVERY segment (never leave a
      // half date like 2026-mm-10 on screen) and save the cleared date.
      clearSegments();
      if (committedRef.current !== "") {
        committedRef.current = "";
        context.onDateChange(data.id, "");
      }
      return;
    }
    // Year range guard (1900–2095): out-of-range → revert to the last committed.
    const yr = Number(v.slice(0, 4));
    if (!(yr >= 1900 && yr <= 2095)) {
      setVal(committedRef.current);
      if (committedRef.current === "") clearSegments();
      return;
    }
    if (v !== committedRef.current) {
      committedRef.current = v;
      context.onDateChange(data.id, v);
    }
  };
  const commit = () => commitValue(val);

  // Commit on the NATIVE `change` event — it fires when a calendar day is
  // PICKED and on blur-after-edit, but NOT while browsing months. That gives an
  // instant row recolor on a picker selection (React onChange couldn't: it maps
  // to the input-event stream, which also fires during month browsing and would
  // re-render the row → close the popup). Kept in a ref so the listener attaches
  // once yet always calls the latest closure.
  const commitRef = useRef(commitValue);
  commitRef.current = commitValue;
  const entryDoneRef = useRef(context.onEntryCommitDone);
  entryDoneRef.current = context.onEntryCommitDone;
  // Timestamp of the last keystroke — Chrome ALSO fires native `change` per
  // completed segment while TYPING; a recent keydown means "typing", which
  // keeps committing on blur/Enter/Tab only (mid-typing commits clobbered a
  // half-entered year, e.g. "0002" on the way to "2026").
  const lastKeyRef = useRef(0);
  useEffect(() => {
    const el = dateInputRef.current;
    if (!el) return undefined;
    const handler = () => {
      if (Date.now() - lastKeyRef.current <= 250) return;
      commitRef.current(el.value);
      setTimeout(() => {
        const live = dateInputRef.current;
        const ae = document.activeElement;
        const focusHere =
          live && (ae === live || !ae || ae === document.body || !ae.isConnected);
        if (!focusHere) return; 
        if (entryDoneRef.current?.()) return; 
        if (ae !== live) live.focus(); 
      }, 0);
    };
    el.addEventListener("change", handler);
    return () => el.removeEventListener("change", handler);
  }, []);

  return (
    <input
      type="date"
      lang="en-CA"
      className="nft-date-btn"
      ref={dateInputRef}
      value={val}
      disabled={locked}
      min="1900-01-01"
      max="2095-12-31"
      onFocus={() => {
        focusedRef.current = true;
      }}
      // Display-only: React onChange maps to the native INPUT event, which
      // Chromium also fires while BROWSING the mini-calendar (each month-arrow
      // click updates the value) — committing here re-rendered the row and
      // closed the popup mid-browse. The commit hangs off the native CHANGE
      // event instead (attached below), which only fires when a date is
      // actually committed (day picked / input left).
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        lastKeyRef.current = Date.now();
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
          e.target.blur?.();
          context.onEntryCommitDone?.();
        }
        if (e.key === "Escape") {
          // Cancel edit, keep stored date; stored empty → wipe any partial segments.
          setVal(data?.finishDate || "");
          if (!(data?.finishDate || "")) clearSegments();
        }
        if (e.key === "Tab") {
          e.preventDefault();
          e.stopPropagation();
          const idx = e.target.closest(".ag-row")?.getAttribute("row-index");
          const backwards = e.shiftKey;
          commit();
          focusAfterCommit(() =>
            finishedRowSel(idx)?.querySelector(
              backwards ? ".nft-time-btn" : ".MuiSelect-select",
            ),
          );
        }
      }}
      style={{
        border: "1px solid #ccc",
        borderRadius: 4,
        padding: compactTablet ? "2px 2px" : "4px 4px",
        fontSize: compact768 ? 11 : 12,
        fontFamily: "inherit",
        // Locked (DNC/abandon): keep the normal text color — only editing is
        // disabled, the date must not grey out.
        color: "#1a3a5c",
        cursor: locked ? "not-allowed" : undefined,
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    />
  );
});
